import { Icon, Typography, Modal, Notification } from '@smartgift/frontend-toolkit';
import { useFormik } from 'formik';
import React from 'react';

import { ValidationErrors } from '@hero/components/molecules';
import { NOTIFICATION_STATUS } from '@hero/constants';
import { useNotification } from '@hero/store/hooks';
import { useValidateRules } from '@hero/store/hooks/useValidateRules';
import { isString } from '@hero/utils/validationUtils';

import { useValidationErrorsStore } from '../ValidationErrorsProvider';

import { NotificationForm } from './NotificationForm';
import * as S from './style';

function NotificationProvider() {
  const {
    popupStyle,
    header,
    notification,
    notificationStatus,
    notificationImage,
    isOpen,
    title,
    message,
    customContent,
    primaryButtonProps,
    onSubmit,
    secondaryButtonProps,
    messageStyle,
    formStyle,
    onClose,
    formData = [],
    formValidationSchema,
    reset,
    formSettings,
    close,
    bodyStyle,
    size,
    onBack,
    modalProps = {}
  } = useNotification();

  const initialValues = formData?.reduce((acc, data) => {
    acc[data?.name] = data?.value || data?.defaultValue || '';
    return acc;
  }, {});

  const { validate } = useValidateRules();
  const { reset: resetValidationErrors } = useValidationErrorsStore();

  const formik = useFormik({
    initialValues: initialValues ?? {},
    validationSchema: formValidationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: async values => {
      if (onSubmit && values) {
        const clickResponse = await onSubmit({ formValues: values, validate });
        if (clickResponse === true) return;
      }

      closeNotification({ forceClose: true });
    },
    ...formSettings
  });

  const closeNotification = ({ forceClose = false } = {}) => {
    resetValidationErrors();
    if (formik.isSubmitting && !forceClose) return;

    if (onClose) onClose();
    formik.resetForm();
    close();

    setTimeout(() => {
      reset();
    }, 100);
  };

  const renderImage = () => {
    if (notificationImage) {
      if (isString(notificationImage)) {
        return <Icon name={notificationImage} size="xxl" />;
      }

      return notificationImage;
    }

    switch (notificationStatus) {
      case NOTIFICATION_STATUS.success:
        return <Icon name="rocket" size="xxl" color="var(--color-success-default)" />;
      case NOTIFICATION_STATUS.error:
        return <Icon name="error" size="xxl" color="var(--color-error-default)" />;
      default:
        return null;
    }
  };

  return (
    isOpen && (
      <Modal
        isOpen={isOpen}
        title={header}
        primaryButtonProps={{
          disabled: !formik?.isValid,
          endIcon: primaryButtonProps.endIcon && <Icon name={primaryButtonProps.endIcon} />,
          loading: formik.isSubmitting,
          ...primaryButtonProps,
          onClick: e => {
            formik?.submitForm?.();
            primaryButtonProps?.onClick?.(e);
          }
        }}
        secondaryButtonProps={{
          disabled: formik.isSubmitting,
          ...secondaryButtonProps,
          onClick: () => {
            if (secondaryButtonProps?.onClick) secondaryButtonProps?.onClick?.();
            else closeNotification();
          }
        }}
        size={size}
        onRequestClose={closeNotification}
        onBack={onBack}
        {...modalProps}
      >
        <S.NotificationProvider
          className={notificationStatus}
          style={popupStyle}
          onSubmit={formik.handleSubmit}
        >
          <S.ModalBody style={bodyStyle}>
            <ValidationErrors />
            {notification && <Notification style={{ margin: '0 0 16px 0' }} {...notification} />}
            {renderImage() && <S.ModalImage>{renderImage()}</S.ModalImage>}
            {title && <Typography variant="body1Bold">{title}</Typography>}
            {message && (
              <S.ModalContentText
                variant="body2"
                color="neutral"
                emphasis="500"
                style={messageStyle}
              >
                {message}
              </S.ModalContentText>
            )}
            {formData && formik?.values && (
              <NotificationForm formik={formik} formData={formData} formStyle={formStyle} />
            )}
            {customContent() && (
              <S.ModalCustomContent>
                {customContent({
                  closeNotification: closeNotification
                })}
              </S.ModalCustomContent>
            )}
          </S.ModalBody>
        </S.NotificationProvider>
      </Modal>
    )
  );
}

export { NotificationProvider };
